import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UmssIconButtonComponent } from '../icon-button/icon-button.component';
import { SelectOption, UmssSelectComponent } from '../select/select.component';

@Component({
  selector: 'umss-date-picker-header',
  standalone: true,
  imports: [CommonModule, UmssIconButtonComponent, UmssSelectComponent],
  templateUrl: './date-picker-header.component.html',
  styleUrl: './date-picker-header.component.scss'
})
export class UmssDatePickerHeaderComponent {
  constructor() {}

  /** Content props */
  @Input() currentMonth: number = new Date().getMonth(); // 0-11
  @Input() currentYear: number = new Date().getFullYear();
  @Input() locale: string = 'en-US';

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Small';
  @Input() variant: 'Default' | 'Compact' = 'Default';
  @Input() layout:
    | '1Month'
    | '2Months'
    | '3Months'
    | 'YearAndMonth'
    | 'OnlyMonth'
    | 'OnlyYear' = '1Month';
  @Input() editable: boolean = false;
  @Input() showYearSelector: boolean = false;

  /** State props */
  @Input() disabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  /** Event outputs */
  @Output() previousMonth = new EventEmitter<void>();
  @Output() nextMonth = new EventEmitter<void>();
  @Output() monthChange = new EventEmitter<number>();
  @Output() yearChange = new EventEmitter<number>();
  @Output() monthYearClick = new EventEmitter<void>();

  /** Computed properties */
  get currentMonthName(): string {
    return new Date(this.currentYear, this.currentMonth).toLocaleDateString(
      this.locale,
      {
        month: 'long'
      }
    );
  }

  get currentMonthShort(): string {
    return new Date(this.currentYear, this.currentMonth).toLocaleDateString(
      this.locale,
      {
        month: 'short'
      }
    );
  }

  get displayText(): string {
    switch (this.layout) {
      case 'OnlyMonth':
        return this.currentMonthName;
      case 'OnlyYear':
        return this.currentYear.toString();
      case 'YearAndMonth':
        return `${this.currentMonthName} ${this.currentYear}`;
      case '2Months': {
        const nextMonth = new Date(this.currentYear, this.currentMonth + 1);
        const nextMonthName = nextMonth.toLocaleDateString(this.locale, {
          month: 'long'
        });
        const nextYear = nextMonth.getFullYear();
        return `${this.currentMonthName} ${this.currentYear}    ${nextMonthName} ${nextYear}`;
      }
      case '3Months': {
        const month2 = new Date(this.currentYear, this.currentMonth + 1);
        const month3 = new Date(this.currentYear, this.currentMonth + 2);
        const month2Name = month2.toLocaleDateString(this.locale, {
          month: 'long'
        });
        const month3Name = month3.toLocaleDateString(this.locale, {
          month: 'long'
        });
        return `${this.currentMonthName} ${this.currentYear}    ${month2Name} ${month2.getFullYear()}    ${month3Name} ${month3.getFullYear()}`;
      }
      default:
        if (this.variant === 'Compact') {
          return `${this.currentMonthShort} ${this.currentYear}`;
        }
        return `${this.currentMonthName} ${this.currentYear}`;
    }
  }

  get showNavigationArrows(): boolean {
    // Show arrows for all layouts when editable, or for standard layouts
    if (this.editable) {
      return true; // Always show arrows when editable
    }
    return this.layout !== 'OnlyMonth' && this.layout !== 'OnlyYear';
  }

  get isMultiMonth(): boolean {
    return this.layout === '2Months' || this.layout === '3Months';
  }

  get canNavigatePrevious(): boolean {
    if (!this.minDate) return true;
    const previousMonth = new Date(this.currentYear, this.currentMonth - 1);
    return (
      previousMonth >=
      new Date(this.minDate.getFullYear(), this.minDate.getMonth())
    );
  }

  get canNavigateNext(): boolean {
    if (!this.maxDate) return true;
    const nextMonth = new Date(this.currentYear, this.currentMonth + 1);
    return (
      nextMonth <= new Date(this.maxDate.getFullYear(), this.maxDate.getMonth())
    );
  }

  /** SVG Icons from Figma */
  get previousArrowSvg(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10.5302 12.4693C10.6711 12.6102 10.7502 12.8013 10.7502 13.0006C10.7502 13.1998 10.6711 13.3909 10.5302 13.5318C10.3893 13.6727 10.1982 13.7519 9.99891 13.7519C9.79965 13.7519 9.60856 13.6727 9.46766 13.5318L4.46766 8.53182C4.39774 8.46214 4.34226 8.37935 4.30441 8.28818C4.26656 8.19702 4.24707 8.09928 4.24707 8.00057C4.24707 7.90186 4.26656 7.80412 4.30441 7.71296C4.34226 7.62179 4.39774 7.539 4.46766 7.46932L9.46766 2.46932C9.60856 2.32842 9.79965 2.24927 9.99891 2.24927C10.1982 2.24927 10.3893 2.32842 10.5302 2.46932C10.6711 2.61021 10.7502 2.80131 10.7502 3.00057C10.7502 3.19983 10.6711 3.39092 10.5302 3.53182L6.06204 7.99994L10.5302 12.4693Z" fill="currentColor"/>
    </svg>`;
  }

  get nextArrowSvg(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M11.5306 8.5306L6.5306 13.5306C6.3897 13.6715 6.19861 13.7506 5.99935 13.7506C5.80009 13.7506 5.60899 13.6715 5.4681 13.5306C5.3272 13.3897 5.24805 13.1986 5.24805 12.9993C5.24805 12.8001 5.3272 12.609 5.4681 12.4681L9.93747 7.99997L5.46935 3.5306C5.39958 3.46083 5.34424 3.37801 5.30649 3.28686C5.26873 3.19571 5.2493 3.09801 5.2493 2.99935C5.2493 2.90069 5.26873 2.80299 5.30649 2.71184C5.34424 2.62069 5.39958 2.53786 5.46935 2.4681C5.53911 2.39833 5.62194 2.34299 5.71309 2.30524C5.80424 2.26748 5.90194 2.24805 6.0006 2.24805C6.09926 2.24805 6.19696 2.26748 6.28811 2.30524C6.37926 2.34299 6.46208 2.39833 6.53185 2.4681L11.5318 7.4681C11.6017 7.53786 11.6571 7.62072 11.6948 7.71193C11.7326 7.80313 11.7519 7.9009 11.7518 7.99961C11.7517 8.09832 11.7321 8.19604 11.6941 8.28715C11.6562 8.37827 11.6006 8.461 11.5306 8.5306Z" fill="currentColor"/>
    </svg>`;
  }

  get dropdownIndicatorSvg(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="13" viewBox="0 0 9 13" fill="none">
      <path d="M1 8.5L4.33334 11.8333L7.6667 8.5M1 4.50008L4.33334 1.16675L7.6667 4.50008" stroke="#4361EE" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  /** Select options for month and year */
  get monthOptions(): SelectOption[] {
    const months: SelectOption[] = [];
    for (let i = 0; i < 12; i++) {
      const monthName = new Date(2024, i).toLocaleDateString(this.locale, {
        month: 'short'
      });
      months.push({
        value: i,
        label: monthName,
        disabled: this.isMonthDisabled(i)
      });
    }
    return months;
  }

  get yearOptions(): SelectOption[] {
    const currentYear = new Date().getFullYear();
    const startYear = this.minDate
      ? this.minDate.getFullYear()
      : currentYear - 50;
    const endYear = this.maxDate
      ? this.maxDate.getFullYear()
      : currentYear + 50;

    const years: SelectOption[] = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push({
        value: year,
        label: year.toString(),
        disabled: this.isYearDisabled(year)
      });
    }
    return years;
  }

  private isMonthDisabled(month: number): boolean {
    if (!this.minDate && !this.maxDate) return false;

    const testDate = new Date(this.currentYear, month);

    if (this.minDate) {
      const minDate = new Date(
        this.minDate.getFullYear(),
        this.minDate.getMonth()
      );
      if (testDate < minDate) return true;
    }

    if (this.maxDate) {
      const maxDate = new Date(
        this.maxDate.getFullYear(),
        this.maxDate.getMonth()
      );
      if (testDate > maxDate) return true;
    }

    return false;
  }

  private isYearDisabled(year: number): boolean {
    if (!this.minDate && !this.maxDate) return false;

    if (this.minDate && year < this.minDate.getFullYear()) return true;
    if (this.maxDate && year > this.maxDate.getFullYear()) return true;

    return false;
  }

  /** Event handlers */
  onPreviousMonth(): void {
    if (!this.disabled && this.canNavigatePrevious) {
      this.previousMonth.emit();
    }
  }

  onNextMonth(): void {
    if (!this.disabled && this.canNavigateNext) {
      this.nextMonth.emit();
    }
  }

  onMonthYearClick(): void {
    if (!this.disabled) {
      this.monthYearClick.emit();
    }
  }

  /** Select event handlers */
  onMonthSelect(month: number): void {
    if (!this.disabled) {
      this.monthChange.emit(month);
    }
  }

  onYearSelect(year: number): void {
    if (!this.disabled) {
      this.yearChange.emit(year);
    }
  }
}
